import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import React, { useState } from 'react';
import { Button, Spinner, Stack } from 'react-bootstrap';
import { BsCheck } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import CurrencyCell from '../../../../components/tables/cellComponents/CurrencyCell';
import { useCartContext } from '../../../../context';
import { useToast } from '../../../../context/ToastContext';
import {
  useDeleteOrderMutation,
  useUpdateOrderTransactionMutation
} from '../../../../services/endpoints/orders/orders';
import { useCreateStripeIntentMutation } from '../../../../services/endpoints/stripe/stripe';
import { useAddWaitlistMutation } from '../../../../services/endpoints/schedule/waitlist';
import { Discount, LocationOrders, PromotionalCode } from '../../../../types/orders.type';
import { getErrorMessage } from '../../../../utils/utils';

type FullOrderCapacityInfo = {
  order_id: number;
  course_id: number;
  child_ids: number[];
};

type UnpayableOrderInfo = {
  order_id: number;
  course_id?: number;
  reason: 'abandoned' | 'session_ended' | 'course_not_open';
};

function getOrderSessionTitle(
  fullOrder: { order_id: number; course_id?: number },
  locationOrders?: LocationOrders,
): string {
  const match =
    locationOrders?.orders.find((o) => o.id === fullOrder.order_id) ??
    locationOrders?.orders.find((o) => o.course_id === fullOrder.course_id);
  return match?.course?.title?.trim() || `Session ${fullOrder.course_id ?? fullOrder.order_id}`;
}

function buildSessionFullMessage(
  fullOrders: FullOrderCapacityInfo[],
  locationOrders?: LocationOrders,
): { message: string; sessionTitles: string[] } {
  const sessionTitles = fullOrders.map((o) => getOrderSessionTitle(o, locationOrders));
  const fullOrderIds = new Set(fullOrders.map((o) => o.order_id));
  const hasRemainingOrders =
    locationOrders?.orders.some((o) => !fullOrderIds.has(o.id)) ?? false;

  let message: string;
  if (sessionTitles.length === 1) {
    message = `${sessionTitles[0]} is full. Join the waitlist to be notified if a spot opens.`;
  } else {
    message =
      'These sessions are full. Join the waitlist for each, or remove them to pay for the rest.';
  }

  if (hasRemainingOrders) {
    message += ' Your other session(s) will stay in your cart.';
  }

  return { message, sessionTitles };
}

function buildOrderNotPayableMessage(
  unpayableOrders: UnpayableOrderInfo[],
  locationOrders?: LocationOrders,
): { message: string; sessionTitles: string[]; courseIds: number[] } {
  const sessionTitles = unpayableOrders.map((o) => getOrderSessionTitle(o, locationOrders));
  const courseIds = unpayableOrders
    .map((o) => {
      const match =
        locationOrders?.orders.find((order) => order.id === o.order_id) ??
        locationOrders?.orders.find((order) => order.course_id === o.course_id);
      return match?.course?.id ?? o.course_id;
    })
    .filter((id): id is number => id != null);

  let message: string;
  if (sessionTitles.length === 1) {
    message = `${sessionTitles[0]} is no longer available to purchase. Remove it from your cart to continue.`;
  } else {
    message =
      'One or more classes in your cart are no longer available to purchase. Remove them to continue.';
  }

  return { message, sessionTitles, courseIds };
}

interface PaymentFormProps {
  dpmCheckerLink?: string;
  location_id: number;
  locationOrders?: LocationOrders;
  appliedDiscounts?: Discount[];
  promoCode?: PromotionalCode;
  onRecovered?: () => void;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  dpmCheckerLink,
  location_id,
  locationOrders,
  appliedDiscounts,
  promoCode,
  onRecovered
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [createPaymentIntent] = useCreateStripeIntentMutation();
  const [updateOrderTransaction] = useUpdateOrderTransactionMutation();
  const [addWaitlist] = useAddWaitlistMutation();
  const [deleteOrder] = useDeleteOrderMutation();
  const navigate = useNavigate();
  const cartContext = useCartContext();
  const [paymentIntent, setPaymentIntent] = useState<{ clientSecret: string; amount: number }>();
  const { addToast } = useToast();

  const [message, setMessage] = useState<string | null | undefined>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [fullOrders, setFullOrders] = useState<FullOrderCapacityInfo[] | null>(null);
  const [unpayableOrders, setUnpayableOrders] = useState<UnpayableOrderInfo[] | null>(null);

  const resetForRetry = () => {
    // Reset back to the Save state so the user can re-enter card and retry.
    // Stripe doesn't allow cleanly reusing a failed PaymentIntent's client_secret,
    // so we drop it and force a new one to be created on the next attempt.
    setMessage(null);
    setPaymentFailed(false);
    setFullOrders(null);
    setUnpayableOrders(null);
    setCanSubmit(false);
    setPaymentIntent(undefined);
    setIsLoading(false);
  };

  const getSessionFullOrders = (error: any): FullOrderCapacityInfo[] | null => {
    const data = error?.data;
    if (data?.code === 'SESSION_FULL' && Array.isArray(data?.full_orders)) {
      return data.full_orders as FullOrderCapacityInfo[];
    }
    return null;
  };

  const getUnpayableOrders = (error: any): UnpayableOrderInfo[] | null => {
    const data = error?.data;
    if (data?.code === 'ORDER_NOT_PAYABLE' && Array.isArray(data?.unpayable_orders)) {
      return data.unpayable_orders as UnpayableOrderInfo[];
    }
    return null;
  };

  const getPaymentIntent = async () => {
    try {
      if (!locationOrders) return;
      const payload = {
        location_id: locationOrders.location.id,
        orders: locationOrders.orders.map((o) => ({ id: o.id, total: o.cost.price })),
        promoCodeId: promoCode?.id,
        appliedDiscountIds: appliedDiscounts?.map((d) => d.id)
      };
      const pi = await createPaymentIntent(payload).unwrap();

      if (pi.free) {
        const orderIds = locationOrders.orders.map((o) => o.id);
        const courseIds = locationOrders.orders.map((o) => o.course.id).join(',');
        await updateOrderTransaction({ ids: orderIds, transaction_id: 'FREE' }).unwrap();
        navigate(`/cart?complete=${locationOrders.location.id}&course_ids=${courseIds}&order_ids=${orderIds.join(',')}`);
        return;
      }

      if (pi.clientSecret) {
        setPaymentIntent({ clientSecret: pi.clientSecret, amount: pi.amount });
        setCanSubmit(true);
      }
    } catch (error) {
      console.error('createPaymentIntent failed:', error);
      const sessionFull = getSessionFullOrders(error);
      if (sessionFull?.length) {
        const { message: sessionMsg } = buildSessionFullMessage(sessionFull, locationOrders);
        addToast(sessionMsg, 'error');
        setMessage(sessionMsg);
        setPaymentFailed(true);
        setFullOrders(sessionFull);
        return;
      }
      const unpayable = getUnpayableOrders(error);
      if (unpayable?.length) {
        const { message: unpayableMsg } = buildOrderNotPayableMessage(unpayable, locationOrders);
        addToast(unpayableMsg, 'error');
        setMessage(unpayableMsg);
        setPaymentFailed(true);
        setUnpayableOrders(unpayable);
        return;
      }
      const msg = getErrorMessage(error);
      addToast(msg, 'error');
      setMessage(msg);
      setPaymentFailed(true);
    }
  };

  const handleJoinWaitlist = async () => {
    if (!fullOrders?.length) return;

    setIsLoading(true);
    try {
      // Waitlist first, then delete — so a failed delete still leaves kids on the list.
      for (const fullOrder of fullOrders) {
        for (const childId of fullOrder.child_ids) {
          await addWaitlist({
            course_id: fullOrder.course_id,
            child_id: childId
          }).unwrap();
        }
      }

      for (const fullOrder of fullOrders) {
        await deleteOrder(fullOrder.order_id).unwrap();
      }

      cartContext.removeCourses(fullOrders.map((o) => o.course_id));
      addToast('Added to waitlist and removed from cart.', 'success');
      setFullOrders(null);
      setPaymentFailed(false);
      setMessage(null);
      onRecovered?.();
    } catch (error) {
      const msg = getErrorMessage(error);
      addToast(msg, 'error');
      setMessage(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveUnpayable = async () => {
    if (!unpayableOrders?.length) return;

    setIsLoading(true);
    try {
      const { courseIds } = buildOrderNotPayableMessage(unpayableOrders, locationOrders);

      for (const order of unpayableOrders) {
        await deleteOrder(order.order_id).unwrap();
      }

      if (courseIds.length) {
        cartContext.removeCourses(courseIds);
      }
      addToast('Unavailable classes removed from your cart.', 'success');
      setUnpayableOrders(null);
      setPaymentFailed(false);
      setMessage(null);
      onRecovered?.();
    } catch (error) {
      const msg = getErrorMessage(error);
      addToast(msg, 'error');
      setMessage(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleError = (error: any) => {
    setIsLoading(false);
    setMessage(error?.message ?? 'An error occurred.');
    setPaymentFailed(true);
  };

  const handleSubmitCard = async (e: any) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsLoading(true);
    setMessage(null);
    setPaymentFailed(false);
    setFullOrders(null);
    setUnpayableOrders(null);

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();
    if (submitError) {
      handleError(submitError);
      return;
    }

    await getPaymentIntent();
    setIsLoading(false);
  };

  const handleSubmitPayment = async () => {
    if (!stripe || !elements || !paymentIntent?.clientSecret) return;

    setIsLoading(true);
    setMessage(null);
    setPaymentFailed(false);

    const order_ids = locationOrders?.orders.map((o) => o.id).join(',');
    const course_ids = locationOrders?.orders.map((o) => o.course.id).join(',');

    const { error } = await stripe.confirmPayment({
      elements,
      clientSecret: paymentIntent.clientSecret,
      confirmParams: {
        return_url: `${window.location.origin}/cart?location_id=${location_id}&order_ids=${order_ids}&course_ids=${course_ids}`
      }
    });

    // Only reached if there's an immediate error — otherwise the user is
    // redirected to return_url and this code never runs.
    if (error?.type === 'card_error' || error?.type === 'validation_error') {
      setMessage(error.message ?? 'Your card was declined.');
    } else {
      setMessage('An unexpected error occurred. Please try again or contact support.');
    }
    setPaymentFailed(true);
    setIsLoading(false);
  };

  const confirmContent =
    canSubmit && !paymentFailed ? (
      <Stack
        direction="horizontal"
        gap={2}
        className="align-items-center mt-3"
      >
        <Button
          disabled
          variant="primary"
          className="p-0"
        >
          <BsCheck size={28} />
        </Button>
        <h4 className="mb-0">Confirm Payment of</h4>
        <h4 className="mb-0">
          <CurrencyCell value={(paymentIntent?.amount ?? 0) / 100} />
        </h4>
      </Stack>
    ) : null;

  const isSessionFull = !!fullOrders?.length;
  const isUnpayable = !!unpayableOrders?.length;
  const isRecoveryState = isSessionFull || isUnpayable;
  const sessionFullDetails = isSessionFull
    ? buildSessionFullMessage(fullOrders!, locationOrders)
    : null;
  const unpayableDetails = isUnpayable
    ? buildOrderNotPayableMessage(unpayableOrders!, locationOrders)
    : null;

  const buttonLabel = isLoading ? (
    <Spinner
      animation="border"
      size="sm"
    />
  ) : isSessionFull ? (
    'Join Waitlist'
  ) : isUnpayable ? (
    'Remove from Cart'
  ) : paymentFailed ? (
    'Try Again'
  ) : canSubmit ? (
    'Confirm Payment'
  ) : (
    'Pay'
  );

  const buttonHandler = isSessionFull
    ? handleJoinWaitlist
    : isUnpayable
      ? handleRemoveUnpayable
      : paymentFailed
        ? resetForRetry
        : canSubmit
          ? handleSubmitPayment
          : handleSubmitCard;

  return (
    <form id="payment-form">
      <Stack>
        <PaymentElement
          id="payment-element"
          options={{ readOnly: canSubmit && !paymentFailed }}
        />
        {message && (
          <div
            className="text-danger mt-2"
            id="payment-message"
            role="alert"
          >
            {message}
            {sessionFullDetails && sessionFullDetails.sessionTitles.length > 1 && (
              <ul className="mb-0 mt-2 ps-3">
                {fullOrders!.map((fullOrder) => (
                  <li key={fullOrder.order_id}>
                    <strong>{getOrderSessionTitle(fullOrder, locationOrders)}</strong>
                  </li>
                ))}
              </ul>
            )}
            {unpayableDetails && unpayableDetails.sessionTitles.length > 1 && (
              <ul className="mb-0 mt-2 ps-3">
                {unpayableOrders!.map((order) => (
                  <li key={order.order_id}>
                    <strong>{getOrderSessionTitle(order, locationOrders)}</strong>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {confirmContent}
        <Button
          id="submit"
          disabled={isLoading || (!isRecoveryState && (!stripe || !elements))}
          type="button"
          className="text-uppercase mt-3"
          variant={paymentFailed || isRecoveryState ? 'outline-primary' : 'primary'}
          onClick={buttonHandler}
        >
          <span id="button-text">{buttonLabel}</span>
        </Button>
      </Stack>
    </form>
  );
};

export default PaymentForm;
